import { useState } from 'react';

import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import { DateTimePicker } from '@/components/DateTimePicker';
import { inviteExpiryError } from '@/store/invites/invites.mappers';
import type { PendingGuestInvite } from '@/store/invites/invites.store';

import Modal from '../Modal/Modal';

import s from './InviteDeadlineModal.module.scss';

interface InviteDeadlineModalProps {
  /** The invitation whose deadline moves; the modal is open while it is set. */
  invite: PendingGuestInvite | null;
  onClose: () => void;
  /** Rejects when the change failed, and the modal stays open for another try. */
  onSave: (invite: PendingGuestInvite, expiresAt: string) => Promise<void>;
  /** The event's end: the latest deadline that may be picked. */
  maxDate?: Date | null;
}

export function InviteDeadlineModal({
  invite,
  onClose,
  onSave,
  maxDate,
}: InviteDeadlineModalProps) {
  const [deadline, setDeadline] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [openedFor, setOpenedFor] = useState<PendingGuestInvite | null>(null);

  // Starts from the invitation's current deadline each time it opens.
  if (invite !== openedFor) {
    setOpenedFor(invite);
    setDeadline(invite?.expiresAt ?? null);
  }

  // The picker only stops at whole days: a time earlier today is caught here.
  const error = inviteExpiryError(deadline, maxDate);

  const save = async () => {
    if (!invite || !deadline || error) return;
    setIsSaving(true);
    try {
      await onSave(invite, deadline);
      onClose();
    } catch {
      // The caller has already said what went wrong.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={!!invite} onClose={onClose} title="Change RSVP Deadline">
      <div className={s.form}>
        <p className={s.note}>
          {invite?.name || invite?.contact} will get the invitation again with the new deadline.
        </p>
        <DateTimePicker
          label="RSVP By"
          placeholder="Jul 18, 2026"
          value={deadline}
          onChange={setDeadline}
          minDate={new Date()}
          maxDate={maxDate ?? undefined}
          showTimeInput
          error={error}
        />
        <div className={s.actions}>
          <ButtonOutlinedSmall type="button" onClick={onClose}>
            Cancel
          </ButtonOutlinedSmall>
          <ButtonSmall
            type="button"
            disabled={!deadline || !!error || isSaving}
            onClick={() => void save()}
          >
            {isSaving ? 'Saving…' : 'Save Deadline'}
          </ButtonSmall>
        </div>
      </div>
    </Modal>
  );
}
